import type { AcledEvent, ThreatLevel } from '@/types';

// ---------------------------------------------------------------------------
// LiveUAMap API — Real-time conflict event feed
//
// API format (reverse-engineered from official C# SDK):
//   GET https://a.liveuamap.com/api?a=mpts&resid={regionId}&time={unixTs}&count={n}&key={apiKey}
//
// Region IDs (key ones):
//   World=1, Ukraine=0, IsraelPalestine=2, Syria=3, MiddleEast=7, Asia=6,
//   Africa=8, Koreas=9, Iran=66, Yemen=53, Russia=18, Taiwan=142, Lebanon=74
//
// Rate limit: 200 req/day per license
// Cache: 8 min → 180 req/day (within limit)
//
// Activation:
//   1. Email info@liveuamap.com to enable API account
//   2. Set VITE_LIVEUAMAP_KEY=<your_key> in .env
// ---------------------------------------------------------------------------

const API_BASE = 'https://a.liveuamap.com/api';
const CACHE_TTL = 8 * 60_000; // 8 min — keeps daily requests under 200

// Regions to query in rotation (3 per cycle, cycling every 8 min)
// Total: max 3 req/cycle × 180 cycles/day = would exceed; so query only World
// World (resid=1) covers all global events in one call.
const QUERY_REGIONS: { resid: number; label: string }[] = [
  { resid: 1,   label: 'World' },
];

// Severity by event keywords
const CRITICAL_RE = /missile|ballistic|nuclear|airstrike|bomb|explosion|killed|casualt/i;
const HIGH_RE     = /attack|artillery|shelling|troops|offensive|strike|drone|fired/i;
const MEDIUM_RE   = /clash|skirmish|protest|tension|threat|deploy|evacuat/i;

// Country name → ISO code mapping (partial)
const COUNTRY_CODES: Record<string, string> = {
  'Ukraine': 'UA', 'Russia': 'RU', 'Israel': 'IL', 'Palestine': 'PS',
  'Gaza': 'PS', 'Syria': 'SY', 'Yemen': 'YE', 'Iran': 'IR',
  'Lebanon': 'LB', 'Iraq': 'IQ', 'Sudan': 'SD', 'Myanmar': 'MM',
  'China': 'CN', 'Taiwan': 'TW', 'Korea': 'KR', 'North Korea': 'KP',
  'Afghanistan': 'AF', 'Pakistan': 'PK', 'Ethiopia': 'ET', 'Somalia': 'SO',
  'Mali': 'ML', 'Libya': 'LY', 'Venezuela': 'VE', 'Turkey': 'TR',
};

interface LUAEvent {
  id: number | string;
  name: string;
  lat: number;
  lng: number;
  time?: number;      // unix timestamp
  timeDt?: string;   // ISO string
  description?: string;
  eventType?: string;
}

interface LUAResponse {
  success: boolean;
  error_code?: number;
  message?: string;
  events?: LUAEvent[];
}

let _cache: AcledEvent[] | null = null;
let _cacheTs = 0;
let _apiEnabled = true; // set false after 401/403 to stop spamming

export async function fetchLiveUAMapEvents(): Promise<AcledEvent[]> {
  const apiKey = import.meta.env.VITE_LIVEUAMAP_KEY as string | undefined;
  if (!apiKey || !_apiEnabled) return _cache ?? [];

  const now = Date.now();
  if (_cache && now - _cacheTs < CACHE_TTL) return _cache;

  try {
    const results: AcledEvent[] = [];
    const timeNow = Math.floor(now / 1000);

    for (const region of QUERY_REGIONS) {
      const url = `${API_BASE}?a=mpts&resid=${region.resid}&time=${timeNow}&count=50&key=${apiKey}`;
      const res = await fetch(url, { signal: AbortSignal.timeout(15_000) });
      if (!res.ok) throw new Error(`LiveUAMap HTTP ${res.status}`);

      const data = await res.json() as LUAResponse;

      if (!data.success) {
        const code = data.error_code ?? 0;
        if (code === 101 || code === 100) {
          _apiEnabled = false; // bad/no key — stop retrying
          console.warn('[LiveUAMap] Invalid API key — disabling. Check VITE_LIVEUAMAP_KEY');
        } else if (code === 194) {
          console.warn('[LiveUAMap] Account not yet enabled — email info@liveuamap.com');
        } else {
          console.warn(`[LiveUAMap] API error ${code}: ${data.message}`);
        }
        break;
      }

      for (const ev of data.events ?? []) {
        results.push(mapToAcledEvent(ev));
      }
    }

    if (results.length > 0) {
      console.log(`[LiveUAMap] fetched ${results.length} events`);
      _cache = results;
      _cacheTs = now;
    }
    return _cache ?? [];
  } catch (err) {
    console.warn('[LiveUAMap] fetch failed:', err);
    _cacheTs = now; // prevent tight retry loop
    return _cache ?? [];
  }
}

function mapToAcledEvent(ev: LUAEvent): AcledEvent {
  const text = ev.name ?? '';
  const severity: ThreatLevel =
    CRITICAL_RE.test(text) ? 'critical' :
    HIGH_RE.test(text)     ? 'high' :
    MEDIUM_RE.test(text)   ? 'medium' : 'low';

  // Detect country from event name
  const countryEntry = Object.entries(COUNTRY_CODES).find(([name]) =>
    text.includes(name)
  );
  const country = countryEntry?.[0] ?? 'Unknown';

  const eventDate = ev.timeDt
    ? ev.timeDt.slice(0, 10)
    : ev.time
    ? new Date(ev.time * 1000).toISOString().slice(0, 10)
    : new Date().toISOString().slice(0, 10);

  return {
    id: String(ev.id),
    event_date: eventDate,
    event_type: 'Explosions/Remote violence',
    sub_event_type: ev.eventType ?? 'Unknown',
    actor1: 'Unknown',
    actor2: '',
    country,
    location: text.slice(0, 60),
    lat: ev.lat,
    lon: ev.lng,
    fatalities: 0,
    notes: text,
    source: 'LiveUAMap',
    severity,
  };
}

export function isLiveUAMapEnabled(): boolean {
  return Boolean(import.meta.env.VITE_LIVEUAMAP_KEY) && _apiEnabled;
}
